import React, { useState } from 'react';
import { Button, Box, Typography, List, ListItem, ListItemText, FormHelperText } from '@mui/material';

interface FileUploadProps {
  label: string;
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  accept?: string; // e.g., 'application/pdf,image/*'
  maxBytes?: number; // e.g., 10 * 1024 * 1024
  error?: string | null;
  onError?: (message: string | null) => void;
  helperText?: string;
  required?: boolean;
}

const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tif', 'tiff']);
const PDF_EXTS = new Set(['pdf']);

function extensionOf(file: File): string {
  const name = String(file?.name || '');
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function isTypeAllowed(file: File, accept?: string) {
  if (!accept) return true;
  const accepts = accept.split(',').map((s) => s.trim()).filter(Boolean);
  const type = String(file.type || '').toLowerCase();
  const ext = extensionOf(file);

  const mimeOk = accepts.some((rule) => {
    if (rule.endsWith('/*')) {
      const base = rule.replace('/*', '');
      return type.startsWith(`${base}/`);
    }
    return type === rule.toLowerCase();
  });
  if (mimeOk) return true;

  // Empty or generic MIME types (common for some PDFs / phone uploads) — fall back to extension.
  const acceptAllowsImage = accepts.some((r) => r === 'image/*' || r.startsWith('image/'));
  const acceptAllowsPdf = accepts.some((r) => r === 'application/pdf' || r.endsWith('.pdf'));
  if (acceptAllowsPdf && PDF_EXTS.has(ext)) return true;
  if (acceptAllowsImage && IMAGE_EXTS.has(ext)) return true;
  return false;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label, onFileSelect, selectedFile, accept, maxBytes, error, onError, helperText, required,
}) => {
  const [localError, setLocalError] = useState<string | null>(null);

  const reportError = (message: string | null) => {
    setLocalError(message);
    onError?.(message);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    // Allow selecting the same file again after a rejected attempt.
    event.target.value = '';
    if (!file) return;
    if (maxBytes && file.size > maxBytes) {
      reportError(`File too large. Max ${(maxBytes / 1024 / 1024).toFixed(0)}MB`);
      return;
    }
    if (!isTypeAllowed(file, accept)) {
      reportError('Unsupported file type. Use PDF or an image (JPG, PNG, WEBP).');
      return;
    }
    reportError(null);
    onFileSelect(file);
  };

  const shownError = error || localError;

  return (
    <Box sx={{ border: '1px dashed', borderColor: shownError ? 'error.main' : 'grey.500', p: 2, my: 1, borderRadius: 1 }}>
      <Typography variant="subtitle1">{label}{required ? ' *' : ''}</Typography>
      <Button variant="contained" component="label">
        Choose File
        <input type="file" hidden onChange={handleFileChange} accept={accept} />
      </Button>
      {helperText && <FormHelperText sx={{ mt: 0.5 }}>{helperText}</FormHelperText>}
      {selectedFile && (
        <List dense>
          <ListItem>
            <ListItemText primary={selectedFile.name} secondary={`${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`} />
          </ListItem>
        </List>
      )}
      {shownError && <FormHelperText error>{shownError}</FormHelperText>}
    </Box>
  );
};

export default FileUpload;
